import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { NO_CHARACTERISTIC } from "@/lib/scan-ui/characteristic-choice";
import { Camera, ClipboardList, Menu, Trash2, Users } from "lucide-react";
import React, { Suspense } from "react";
import { useParams } from "react-router-dom";

import {
  deleteScan,
  editScanCharacteristic,
  editScanQty,
  finishInventory,
  getExcludedCharacteristics,
  getInventory,
  getProgress,
  listCharacteristicNames,
  listScans,
  listWorkers,
  refreshSnapshot,
  reopenInventory,
  saveExcludedCharacteristics,
  scanInventory,
  type InventoryDetail,
  type InventoryPosition,
  type InventoryScanResult,
} from "@/api/inventory";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { parseScan } from "@/lib/order-scan";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { LoadState, apiErrorText, useGoBack } from "@/lib/scan-ui/screen";
import type { CharOption, ResolveRequest } from "@/api/order_check";
import { toResolveRequest } from "@/lib/scan-ui/scan-resolve";
import { useScanCapture } from "@/lib/use-scan-capture";
import { ScanBanner } from "@/pages/fbs/ScanBanner";
import { CharacteristicAsk } from "@/pages/inventory/CharacteristicAsk";
import { CharacteristicFix, allCharacteristics } from "@/pages/inventory/CharacteristicFix";
import { ExcludedCharacteristicsSheet } from "@/pages/inventory/ExcludedCharacteristicsSheet";
import { LastScanQty } from "@/pages/inventory/LastScanQty";
import { ScanList } from "@/pages/inventory/ScanList";
import { SessionMenu } from "@/pages/inventory/SessionMenu";
import { findSplitPositions, splitVariantText } from "@/pages/inventory/split-positions";
import { getReceivingConfig } from "@/api/receiving";

type Banner = { kind: "ok" | "warn" | "error"; text: string } | null;

/** Метка устройства живёт на самом устройстве: на складе пять одинаковых ТСД,
 *  и без неё ответ на «кто считал эту полку» сводится к «тот, под кем зашли». */
const DEVICE_KEY = "wh_inv_device";
const device = (): string => {
  try {
    return localStorage.getItem(DEVICE_KEY) ?? "";
  } catch {
    return "";
  }
};

/** Сколько расщеплённых номенклатур перечисляем поимённо в диалоге завершения:
 *  список на экране ТСД должен помещаться, остальные — числом. */
const SPLIT_SHOWN = 3;

function posKey(p: { nomenclature_ref: string; characteristic_ref: string; partia_ref: string }) {
  return `${p.nomenclature_ref}|${p.characteristic_ref}|${p.partia_ref}`;
}

/** Карточка последнего скана из ответа сервера. Позиция и `scan_id` тут
 *  обязаны быть: без них карточку строить не из чего — падаем громко, а не
 *  рисуем пустоту. */
function heroOf(res: InventoryScanResult): {
  key: string;
  scanId: string;
  /** Количество ЭТОГО скана — из самого ответа скана (коробка/GS1 AI 30 — не
   *  1). `null` — бэк его не прислал: карточка обязана показать ошибку, а не
   *  подставить 1. */
  qty: number | null;
  autofilled: boolean;
  characteristicName: string;
  characteristics: CharOption[];
} {
  if (!res.position || !res.scan_id) {
    throw new Error("Скан записан без позиции — карточку последнего скана строить не из чего.");
  }
  return {
    key: posKey(res.position),
    scanId: res.scan_id,
    qty: res.scan_qty ?? null,
    autofilled: res.characteristic_autofilled,
    characteristicName: res.characteristic_name,
    characteristics: allCharacteristics(res),
  };
}

/** Ключ позиции после смены характеристики: позиция ПЕРЕЕЗЖАЕТ, и карточка
 *  «что я только что посчитал» обязана показать новую строку, а не исчезнуть.
 *  ⚠️ `none` — это ответ «без характеристики», и в позиции он пустая ссылка. */
function keyWithCharacteristic(key: string, ref: string): string {
  const [nom = "", , partia = ""] = key.split("|");
  return `${nom}|${ref === NO_CHARACTERISTIC ? "" : ref}|${partia}`;
}

type CounterKey = "counted" | "left" | "surplus" | "shortage";

/** Счётчики сводки и списки, которые они открывают. «Не считали» — отдельно
 *  от недостачи: это не расхождение, а незакрытая работа. Излишки — вместе с
 *  находками вне снимка, как и число в счётчике (`surplus + unexpected`). */
const COUNTERS: Record<
  CounterKey,
  { title: string; empty: string; match: (p: InventoryPosition) => boolean }
> = {
  counted: { title: "посчитано", empty: "Ещё ничего не посчитано.", match: (p) => p.counted },
  left: {
    title: "не считали",
    empty: "Все позиции посчитаны.",
    match: (p) => !p.counted && !p.unexpected,
  },
  surplus: {
    title: "излишки",
    empty: "Излишков нет.",
    match: (p) => p.unexpected || (p.counted && p.deviation > 0),
  },
  shortage: {
    title: "недостача",
    empty: "Недостач нет.",
    match: (p) => p.counted && p.deviation < 0,
  },
};

/** Как выглядит позиция в списке: находка → расхождение → не считали → сошлось. */
function tone(p: InventoryPosition): { cls: string; label: string } {
  if (p.unexpected) return { cls: "border-sky-300 bg-sky-50", label: "нет в учёте" };
  if (!p.counted) return { cls: "border-slate-200 bg-white", label: "не считали" };
  if (p.deviation > 0) return { cls: "border-amber-300 bg-amber-50", label: `излишек +${p.deviation}` };
  if (p.deviation < 0) return { cls: "border-rose-300 bg-rose-50", label: `недостача ${p.deviation}` };
  return { cls: "border-emerald-200 bg-emerald-50", label: "сошлось" };
}

/**
 * Пересчёт склада под ТСД. Один вьюпорт, поля ввода ШК нет — код ловит
 * глобальный перехват (`use-scan-capture`), как на всех скан-экранах портала.
 *
 * Организация и склад видны в шапке ВСЮ смену: «что мы инвентим» перестаёт
 * быть очевидным через час, а ошибка тут стоит документа по чужому складу.
 */
export function InventorySessionPage(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const { sessionId = "" } = useParams();
  const qc = useQueryClient();
  const goBack = useGoBack("/inventory");

  const [banner, setBanner] = React.useState<Banner>(null);
  const [sheet, setSheet] = React.useState<
    null | "list" | "feed" | "workers" | "menu" | "excluded"
  >(null);
  const [confirm, setConfirm] = React.useState<null | "finish" | "reopen">(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  // Уточнение характеристики: держим И ответ, И ИСХОДНЫЙ запрос — повтор
  // должен нести тот же код, иначе бэку резолвить нечего.
  const [pending, setPending] = React.useState<
    {
      res: InventoryScanResult;
      req: ResolveRequest;
      /** Уже данный ответ о характеристике — повтор обязан нести его дальше. */
      characteristic_ref?: string;
    } | null
  >(null);
  // Герой хранит КЛЮЧ позиции, а не её копию: смена общая, и пока карточка
  // висит на экране, эту же позицию считает сосед. Копия показывала бы числа
  // на момент моего скана — то есть врала бы ровно там, где важнее всего.
  const [last, setLast] = React.useState<{
    key: string;
    scanId: string;
    /** Количество ЭТОГО скана — из ответа скана, не запросом. `null` — ошибка
     *  (бэк не прислал), карточка показывает её вместо степпера. */
    qty: number | null;
    /** Характеристику подставил ПОРТАЛ (правило склада оставило один вариант) —
     *  карточка обязана это показать и дать «изменить». */
    autofilled: boolean;
    characteristicName: string;
    /** ВСЕ варианты кода — вместе с отброшенными правилом: главный случай
     *  ошибки («сканировали ОПТ, подставилось без характеристики») чинится
     *  отсюда, а не походом в настройку. */
    characteristics: CharOption[];
  } | null>(null);
  // Шторка «изменить характеристику» у карточки последнего скана.
  const [charEdit, setCharEdit] = React.useState(false);
  // Карточка позиции держит КЛЮЧ по той же причине, что и герой: пока шторка
  // открыта, эту позицию считает сосед, а числа в ней обязаны быть текущими.
  const [posSel, setPosSel] = React.useState<null | {
    nomenclature_ref: string;
    characteristic_ref: string;
    partia_ref: string;
    /** Имя на заголовок шторки: удалив последний скан НАХОДКИ (её нет в
     *  снимке), позицию из списка мы теряем, а шторка остаётся открытой. */
    title: string;
  }>(null);
  // Счётчик сводки — переключатель: открыт максимум один список, по
  // умолчанию ни одного (экран принадлежит скану, список — по запросу).
  const [openList, setOpenList] = React.useState<CounterKey | null>(null);

  const detail = useQuery({
    queryKey: ["inventory", sessionId],
    queryFn: () => getInventory(sessionId),
  });
  const config = useQuery({ queryKey: ["receiving-config"], queryFn: getReceivingConfig });
  const d: InventoryDetail | undefined = detail.data;
  const readOnly = d?.status !== "open";

  const refresh = () => qc.invalidateQueries({ queryKey: ["inventory", sessionId] });
  /** Лента и сканы позиции живут своими ключами: без отдельной инвалидации
   *  шторка после удаления продолжала бы показывать удалённую строку. */
  const refreshScans = () => {
    void qc.invalidateQueries({ queryKey: ["inventory-feed", sessionId] });
    void qc.invalidateQueries({ queryKey: ["inventory-position-scans", sessionId] });
  };

  const scanMethod = config.data?.scan_method ?? "tsd";
  const cameraAllowed = scanMethod === "camera" || scanMethod === "both";

  const scan = useMutation({
    mutationFn: async (raw: string) => {
      const parsed = parseScan(raw);
      const req = toResolveRequest(parsed);
      if (!req) throw new Error("Код не распознан. Отсканируйте ещё раз.");
      const res = await scanInventory(sessionId, { ...req, device: device() });
      return { res, req };
    },
    onSuccess: ({ res, req }) => {
      if (!res.ok && (res.needs_characteristic || res.needs_partia)) {
        setPending({ res, req });
        return;
      }
      if (!res.ok) {
        setBanner({ kind: "error", text: res.message || "Код не опознан." });
        return;
      }
      if (res.position && res.scan_id) setLast(heroOf(res));
      // ⚠️ Жёлтым — ТОЛЬКО по `warning` (предупреждение правила склада; `warn`
      // сам не гаснет, в отличие от `ok`). По `message` красить нельзя: на
      // КАЖДЫЙ успешный скан коробки он несёт «засчитано позиций — N», и
      // жёлтая плашка на нормальной работе читается на ТСД как «что-то не
      // так».
      setBanner(res.warning ? { kind: "warn", text: res.warning } : null);
      refresh();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  /** Повтор скана с уточнением — тем же кодом, что и первый раз.
   *
   *  Уточнения НАКАПЛИВАЮТСЯ: сначала характеристика, потом партия. Если бы
   *  повтор нёс только последний ответ, после ввода партии портал спросил бы
   *  характеристику снова — и так по кругу. */
  const retry = useMutation({
    mutationFn: (v: {
      req: ResolveRequest;
      characteristic_ref?: string;
      batch?: string;
      accept_without_partia?: boolean;
    }) =>
      scanInventory(sessionId, {
        ...v.req,
        batch: v.batch ?? v.req.batch,
        characteristic_ref: v.characteristic_ref,
        accept_without_partia: v.accept_without_partia,
        device: device(),
      }),
    onSuccess: (res, vars) => {
      if (!res.ok && (res.needs_characteristic || res.needs_partia)) {
        // Ответ принят, но не хватает следующего — держим шторку и ЗАПОМИНАЕМ
        // уже данные ответы в самом запросе.
        setPending({
          res,
          req: { ...vars.req, batch: vars.batch ?? vars.req.batch },
          characteristic_ref: vars.characteristic_ref ?? pending?.characteristic_ref,
        });
        return;
      }
      setPending(null);
      if (!res.ok) {
        setBanner({ kind: "error", text: res.message || "Код не опознан." });
        return;
      }
      if (res.position && res.scan_id) setLast(heroOf(res));
      // Жёлтым — только предупреждение правила, см. `scan.onSuccess`.
      setBanner(res.warning ? { kind: "warn", text: res.warning } : null);
      refresh();
    },
    onError: (e) => {
      setPending(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  // Перехват молчит, пока открыта шторка, камера или диалог: там у человека
  // другой контекст, и код должен попасть в поле, а не в общий разбор.
  // ⚠️ И пока предыдущий скан ЛЕТИТ: ответ идёт около секунды, а ТСД за это
  // время успевает выстрелить повторно (в прод-БД пары сканов одного кода с
  // интервалом 0.4–1.0 с, местами три подряд за 0.55 с) — каждый выстрел
  // становился отдельной строкой пересчёта.
  const scanning = scan.isPending || retry.isPending;
  const captureEnabled =
    Boolean(d) &&
    !readOnly &&
    !sheet &&
    !posSel &&
    !confirm &&
    !cameraOpen &&
    !pending &&
    !charEdit &&
    !scanning;

  const capture = useScanCapture({
    enabled: captureEnabled,
    onScan: (raw) => scan.mutate(raw),
    // ⚠️ Выключенный перехват обязан быть СЛЫШНЫМ: человек видит только
    // экран, и молча съеденный код он засчитает за посчитанный товар.
    // Текст про «ещё записывается» — тот же, что у камеры: случай один.
    onBlocked: () => {
      if (scanning) {
        setBanner({
          kind: "warn",
          text: "Предыдущий скан ещё записывается — отсканируйте ещё раз.",
        });
        return;
      }
      if (!d) {
        setBanner({ kind: "warn", text: "Экран ещё загружается — отсканируйте ещё раз." });
        return;
      }
      if (readOnly) {
        setBanner({ kind: "warn", text: "Инвентаризация завершена — сканы не принимаются." });
        return;
      }
      setBanner({
        kind: "warn",
        text: "Сначала ответьте на вопрос на экране, потом сканируйте.",
      });
    },
  });

  const editQty = useMutation({
    mutationFn: (v: { scanId: string; qty: number }) => editScanQty(sessionId, v.scanId, v.qty),
    onSuccess: (_r, v) => {
      // Герой держит `qty` ИЗ ОТВЕТА СКАНА и сам его больше не обновляет —
      // без этого сохранённое здесь количество проигрывало гонку с `refresh()`:
      // пока запрос позиций не вернулся, `hero` на миг переставал находиться
      // (`positions.find` по старым данным), карточка размонтировалась и
      // монтировалась заново с `initial={last.qty}` — то есть со СТАРЫМ числом
      // из самого первого скана, а не с только что сохранённым.
      setLast((prev) => (prev && prev.scanId === v.scanId ? { ...prev, qty: v.qty } : prev));
      refresh();
      refreshScans();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  /** Скан удалён — уход его карточки НЕ сохраняет количество (PATCH в
   *  удалённый скан — 404 на пустом месте). */
  const droppedScan = React.useRef<string | null>(null);

  const dropScan = useMutation({
    mutationFn: (scanId: string) => deleteScan(sessionId, scanId),
    onSuccess: (_r, scanId) => {
      // Герой гасим ТОЛЬКО когда удалили именно его скан: из ленты удаляют
      // чужую строку, а карточка «что я только что посчитал» к ней отношения
      // не имеет.
      if (last?.scanId === scanId) {
        // Карточка уходит — её несохранённое количество слать уже некуда.
        droppedScan.current = scanId;
        setLast(null);
      }
      refresh();
      refreshScans();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  /** «Изменить» у карточки скана: портал подставил характеристику сам, и
   *  ошибку такой подстановки правят здесь — настройка касается СЛЕДУЮЩИХ
   *  сканов, а этот уже записан. */
  const changeChar = useMutation({
    mutationFn: (v: { scanId: string; option: CharOption }) =>
      editScanCharacteristic(sessionId, v.scanId, {
        // ⚠️ Ссылку шлём КАК ЕСТЬ: `none` разворачивает сервер.
        characteristic_ref: v.option.ref,
        characteristic_name: v.option.ref === NO_CHARACTERISTIC ? "" : v.option.name,
      }),
    onSuccess: (_r, v) => {
      setCharEdit(false);
      const name = v.option.ref === NO_CHARACTERISTIC ? "" : v.option.name;
      setLast((prev) =>
        prev
          ? {
              ...prev,
              key: keyWithCharacteristic(prev.key, v.option.ref),
              autofilled: false,
              characteristicName: name,
            }
          : prev,
      );
      setBanner({
        kind: "ok",
        text: `Характеристика скана изменена: ${name || "без характеристики"}.`,
      });
      refresh();
      refreshScans();
    },
    onError: (e) => {
      setCharEdit(false);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  const resnap = useMutation({
    mutationFn: () => refreshSnapshot(sessionId),
    onSuccess: () => {
      setBanner({ kind: "ok", text: "Учётные остатки пересняты. Посчитанное сохранено." });
      refresh();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const finish = useMutation({
    mutationFn: () => finishInventory(sessionId),
    onSuccess: (r) => {
      setConfirm(null);
      // `d` здесь ещё ДО обновления — по нему и видно, документ создан
      // или обновлён: номер у обновлённого тот же, и «создан» соврало бы.
      setBanner({
        kind: "ok",
        text: d?.one_c_doc_number
          ? `Документ ${r.one_c_doc_number} обновлён (не проведён).`
          : `Создан документ ${r.one_c_doc_number} (не проведён).`,
      });
      refresh();
    },
    onError: (e) => {
      setConfirm(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  const reopen = useMutation({
    mutationFn: () => reopenInventory(sessionId),
    onSuccess: () => {
      setConfirm(null);
      setBanner({
        kind: "ok",
        text: `Инвентаризация снова в работе. Документ ${d?.one_c_doc_number ?? ""} останется тем же — при повторном завершении он обновится.`,
      });
      refresh();
    },
    onError: (e) => {
      setConfirm(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  // Совместная работа: сводку опрашиваем по таймеру, а тяжёлый список
  // перечитываем ТОЛЬКО когда счётчик сканов изменился — то есть когда
  // кто-то действительно что-то посчитал. Опрос молчит на завершённой смене
  // и в фоновой вкладке: ТСД лежит в кармане половину смены.
  const live = useQuery({
    queryKey: ["inventory-progress", sessionId],
    queryFn: () => getProgress(sessionId),
    enabled: Boolean(d) && !readOnly,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const liveScans = live.data?.scans_total;
  const knownScans = d?.progress.scans_total;
  React.useEffect(() => {
    if (liveScans !== undefined && knownScans !== undefined && liveScans !== knownScans) {
      refresh();
    }
    // `refresh` — стабильная обёртка над invalidateQueries, в зависимостях не нужна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveScans, knownScans]);

  const feed = useQuery({
    queryKey: ["inventory-feed", sessionId],
    queryFn: () => listScans(sessionId),
    enabled: sheet === "feed",
  });
  // Правило склада «что не предлагать при скане» и список имён 1С — только
  // пока шторка настройки открыта: список имён идёт запросом в 1С.
  const excluded = useQuery({
    queryKey: ["inventory-excluded", sessionId],
    queryFn: () => getExcludedCharacteristics(sessionId),
    enabled: sheet === "excluded",
  });
  const charNames = useQuery({
    queryKey: ["inventory-char-names", sessionId],
    queryFn: () => listCharacteristicNames(sessionId),
    enabled: sheet === "excluded",
  });
  const saveExcluded = useMutation({
    mutationFn: (names: string[]) => saveExcludedCharacteristics(sessionId, names),
    onSuccess: (r) => {
      setSheet(null);
      setBanner({
        kind: "ok",
        text: r.names.length
          ? `Для склада «${r.warehouse_name}» не предлагаем: ${r.names.join(", ")}.`
          : `Для склада «${r.warehouse_name}» предлагаем все характеристики.`,
      });
      // Перечитываем настройку: следующее открытие шторки обязано показать
      // то, что реально запомнил сервер, а не то, что мы отправили.
      void qc.invalidateQueries({ queryKey: ["inventory-excluded", sessionId] });
      void qc.invalidateQueries({ queryKey: ["inventory-char-names", sessionId] });
    },
    // Ошибку не глотаем: шторка остаётся открытой и показывает её текстом,
    // отметки человека при этом целы.
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const workers = useQuery({
    queryKey: ["inventory-workers", sessionId],
    queryFn: () => listWorkers(sessionId),
    enabled: sheet === "workers",
  });

  // Сканы ОДНОЙ позиции: «кто насчитал эти 137 штук» и что из них лишнее.
  const posScans = useQuery({
    queryKey: [
      "inventory-position-scans",
      sessionId,
      posSel ? posKey(posSel) : "",
    ],
    queryFn: () => {
      if (!posSel) throw new Error("Позиция не выбрана — запрос сканов не должен был уйти.");
      return listScans(sessionId, {
        nomenclature_ref: posSel.nomenclature_ref,
        characteristic_ref: posSel.characteristic_ref,
        partia_ref: posSel.partia_ref,
      });
    },
    enabled: Boolean(posSel),
  });

  useBackGuard(cameraOpen, () => setCameraOpen(false));
  useBackGuard(Boolean(sheet), () => setSheet(null));
  useBackGuard(Boolean(posSel), () => setPosSel(null));
  useBackGuard(charEdit, () => setCharEdit(false));

  // Успех гаснет сам, ошибка — нет: незамеченная ошибка скана это позиция,
  // которую считали и не посчитали.
  React.useEffect(() => {
    if (banner?.kind !== "ok") return;
    const t = window.setTimeout(() => setBanner(null), 4000);
    return () => window.clearTimeout(t);
  }, [banner]);

  const positions = d?.positions ?? [];
  const hero = last ? positions.find((p) => posKey(p) === last.key) ?? null : null;
  const selected = posSel ? positions.find((p) => posKey(p) === posKey(posSel)) ?? null : null;
  const shown = openList ? positions.filter(COUNTERS[openList].match) : [];
  const pr = d?.progress;
  // Одна номенклатура, посчитанная в нескольких вариантах, уедет в 1С
  // несколькими строками — склад читает это как «две одинаковые строки».
  // Показываем перед созданием документа, пока сканы ещё можно поправить.
  const split = React.useMemo(() => findSplitPositions(d?.positions ?? []), [d?.positions]);

  return (
    <div className="flex h-app flex-col bg-slate-100">
      <input ref={capture.scanInputRef} {...capture.scanInputProps} className="sr-only" />

      {/* Z1 — что инвентим. Организация и склад видны всю смену. */}
      <header className="flex items-center gap-3 bg-white px-3 py-2 shadow-sm">
        <button onClick={goBack} className="text-sm text-slate-500">
          ←
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{d?.warehouse_name || "Склад"}</div>
          <div className="truncate text-xs text-slate-500">
            {d?.organization_name}
            {d?.snapshot_at ? ` · остатки на ${fmtDate(d.snapshot_at)}` : ""}
          </div>
        </div>
        {/* Смена общая — сколько людей сейчас в ней, видно сразу: иначе
            «числа сами меняются» читается как сбой портала. */}
        {!readOnly && (pr?.participants.length ?? 0) > 1 && (
          <button
            onClick={() => setSheet("workers")}
            className="shrink-0 rounded-full bg-sky-100 px-2 py-1 text-xs text-sky-900"
            title="Считают несколько человек"
          >
            <Users className="mr-1 inline size-3" />
            {pr?.participants.length}
          </button>
        )}
        {readOnly && (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
            завершена
          </span>
        )}
        {/* Редкие действия смены — под меню: за смену их нажимают по разу, а
            место внизу принадлежит скану. Образец — `fbs/PickPage.tsx`. */}
        {d && (
          <button
            type="button"
            onClick={() => setSheet("menu")}
            aria-label="Меню"
            className="shrink-0 p-1 text-slate-700"
          >
            <Menu className="size-5" />
          </button>
        )}
      </header>

      {(detail.isLoading || detail.error) && (
        <LoadState
          isLoading={detail.isLoading}
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      )}

      {d && (
        <div className="flex min-h-0 flex-1 flex-col">
          {banner && (
            <div className="px-3 pt-3">
              <ScanBanner tone={banner.kind} title={banner.text} onClose={() => setBanner(null)} />
            </div>
          )}

          {/* Z2 — сводка. «Не считали» стоит ОТДЕЛЬНО от недостачи: это не
              расхождение, а незакрытая работа. Счётчик — переключатель
              списка под ним (`COUNTERS`). */}
          {pr && (
            <div className="grid grid-cols-4 gap-1 px-3 pt-3 text-center">
              {(
                [
                  { k: "counted", n: pr.positions_counted, of: pr.positions_total },
                  { k: "left", n: pr.positions_untouched },
                  { k: "surplus", n: pr.surplus_positions + pr.unexpected_positions },
                  { k: "shortage", n: pr.shortage_positions },
                ] as { k: CounterKey; n: number; of?: number }[]
              ).map((c) => {
                const active = openList === c.k;
                return (
                  <button
                    key={c.k}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setOpenList(active ? null : c.k)}
                    className={
                      "rounded-lg p-2 shadow-sm " +
                      (active ? "bg-slate-900 text-white ring-2 ring-slate-900" : "bg-white")
                    }
                  >
                    <div className="text-lg font-bold leading-none">
                      {c.n}
                      {c.of !== undefined && (
                        <span
                          className={
                            "text-xs font-normal " + (active ? "text-slate-300" : "text-slate-400")
                          }
                        >
                          {" "}
                          / {c.of}
                        </span>
                      )}
                    </div>
                    <div className={"text-[10px] " + (active ? "text-slate-200" : "text-slate-500")}>
                      {COUNTERS[c.k].title}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Z3 — герой: что только что отсканировали. */}
          <div className="px-3 pt-3">
            {hero && last ? (
              <div className="rounded-xl border-2 border-slate-300 bg-white p-3 shadow-sm">
                <div className="text-sm font-semibold leading-tight">
                  {hero.nomenclature_name || "Позиция"}
                </div>
                <div className="text-xs text-slate-500">
                  {hero.nomenclature_article}
                  {hero.characteristic_name ? ` · ${hero.characteristic_name}` : ""}
                  {hero.partia_name ? ` · партия ${hero.partia_name}` : ""}
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <div>
                    <span className="text-2xl font-bold">{hero.qty_scanned}</span>
                    <span className="text-xs text-slate-500"> {hero.unit_name} насчитано</span>
                  </div>
                  {hero.unexpected ? (
                    <span className="rounded bg-sky-100 px-2 py-0.5 text-xs text-sky-900">
                      в учёте этой позиции нет
                    </span>
                  ) : (
                    <span className="text-xs text-slate-600">
                      по учёту {hero.qty_accounted}
                      {hero.remaining > 0
                        ? ` · осталось ${hero.remaining}`
                        : hero.remaining < 0
                          ? ` · перебор ${-hero.remaining}`
                          : " · сошлось"}
                    </span>
                  )}
                </div>
                {/* Портал подставил характеристику сам — это видно, и это
                    можно исправить в один-два тапа. */}
                {last.autofilled && !readOnly && (
                  <CharacteristicFix
                    name={last.characteristicName}
                    options={last.characteristics}
                    open={charEdit}
                    busy={changeChar.isPending}
                    onOpen={() => setCharEdit(true)}
                    onClose={() => setCharEdit(false)}
                    onPick={(option) => changeChar.mutate({ scanId: last.scanId, option })}
                  />
                )}
                {!readOnly && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500">в этом скане:</span>
                    {last.qty != null ? (
                      <LastScanQty
                        key={last.scanId}
                        initial={last.qty}
                        busy={editQty.isPending}
                        onSave={(qty) => editQty.mutateAsync({ scanId: last.scanId, qty })}
                        onLeave={(qty) => {
                          if (droppedScan.current === last.scanId) return;
                          editQty.mutate({ scanId: last.scanId, qty });
                        }}
                        onDone={() => setLast(null)}
                      />
                    ) : (
                      <span className="text-xs text-red-700">
                        Количество этого скана не пришло с сервера — обновите экран.
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dropScan.mutate(last.scanId)}
                      title="Удалить этот скан"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                {readOnly
                  ? "Инвентаризация завершена."
                  : "Сканируйте товар — код ЧЗ, штрихкод штуки или короба."}
              </div>
            )}
          </div>

          {/* Z4 — список, открытый счётчиком сводки. Ничего не открыто —
              место остаётся пустым: экран принадлежит скану. */}
          <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
            {shown.map((p) => {
              const t = tone(p);
              return (
                // Тап по строке открывает её сканы: ошибочный скан ищут
                // именно отсюда — «вот эта позиция насчитана неверно».
                <button
                  key={posKey(p)}
                  type="button"
                  onClick={() =>
                    setPosSel({
                      nomenclature_ref: p.nomenclature_ref,
                      characteristic_ref: p.characteristic_ref,
                      partia_ref: p.partia_ref,
                      title: p.nomenclature_name,
                    })
                  }
                  className={`w-full rounded-lg border p-2 text-left ${t.cls}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium">{p.nomenclature_name}</div>
                      <div className="truncate text-[11px] text-slate-500">
                        {p.nomenclature_article}
                        {p.characteristic_name ? ` · ${p.characteristic_name}` : ""}
                        {p.partia_name ? ` · п. ${p.partia_name}` : ""}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-semibold">
                        {p.qty_scanned}
                        <span className="text-[11px] font-normal text-slate-400">
                          {" "}
                          / {p.qty_accounted}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500">{t.label}</div>
                    </div>
                  </div>
                </button>
              );
            })}
            {openList && shown.length === 0 && (
              <p className="p-4 text-center text-sm text-slate-500">
                {COUNTERS[openList].empty}
              </p>
            )}
          </div>

          {/* Z5 — скан. Главное действие экрана — одно и большое; лента
              сканов маленькая рядом, остальное ушло в меню шапки.
              ⚠️ Кнопки «Сканировать» НЕТ там, где камера не разрешена
              (`scan_method = tsd`) или смена уже завершена: сканируют
              аппаратно, и кнопка, которая ничего не откроет, — обман. */}
          <div className="flex gap-2 border-t bg-white p-3">
            <Button variant="outline" onClick={() => setSheet("feed")} title="Лента сканов">
              <ClipboardList className="size-4" />
            </Button>
            {cameraAllowed && !readOnly && (
              <Button className="h-12 flex-1 text-base" onClick={() => setCameraOpen(true)}>
                <Camera className="mr-1 size-4" />
                Сканировать
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Уточнения: характеристика (код один, размеров несколько) и партия.
          Позиция инвентаризации — (ном, хар, ПАРТИЯ), и посчитанное без любой
          из трёх частей не с чем сводить со снимком учёта. */}
      {pending && (
        <BottomSheet onClose={() => setPending(null)}>
          <div className="max-h-[70dvh] overflow-y-auto p-4">
            <div className="mb-2 text-sm font-semibold">
              {pending.res.nomenclature_name || "Товар"}
              {pending.res.characteristic_name && (
                <span className="text-slate-500"> · {pending.res.characteristic_name}</span>
              )}
            </div>
            {pending.res.needs_characteristic ? (
              /* Вопрос целиком — в `CharacteristicAsk`: там же живёт
                 раскрытие отброшенных правилом вариантов (правило —
                 подсказка, а не запрет). */
              <CharacteristicAsk
                res={pending.res}
                busy={retry.isPending}
                onPick={(c) =>
                  retry.mutate({ req: pending.req, characteristic_ref: c.ref })
                }
              />
            ) : (
              <PartiaAsk
                result={pending.res}
                busy={retry.isPending}
                onSubmit={(batch) =>
                  retry.mutate({
                    req: pending.req,
                    characteristic_ref: pending.characteristic_ref,
                    batch,
                  })
                }
                onWithout={() =>
                  retry.mutate({
                    req: pending.req,
                    characteristic_ref: pending.characteristic_ref,
                    batch: pending.res.partia_name,
                    accept_without_partia: true,
                  })
                }
              />
            )}
          </div>
        </BottomSheet>
      )}

      {sheet === "menu" && d && (
        <SessionMenu
          readOnly={readOnly}
          snapshotAt={d.snapshot_at}
          docNumber={d.one_c_doc_number}
          participants={pr?.participants.length ?? 0}
          resnapping={resnap.isPending}
          onClose={() => setSheet(null)}
          onFinish={() => setConfirm("finish")}
          onReopen={() => setConfirm("reopen")}
          onResnap={() => resnap.mutate()}
          onWorkers={() => setSheet("workers")}
          onExcluded={() => {
            // Ошибка прошлого сохранения — прошлая: показывать её на свежей
            // шторке значило бы пугать человека несуществующим сбоем.
            saveExcluded.reset();
            setSheet("excluded");
          }}
        />
      )}

      {sheet === "excluded" && d && (
        <ExcludedCharacteristicsSheet
          warehouseName={d.warehouse_name}
          names={charNames.data}
          excluded={excluded.data?.names}
          loading={excluded.isLoading || charNames.isLoading}
          loadError={
            excluded.error || charNames.error
              ? apiErrorText(excluded.error ?? charNames.error)
              : null
          }
          saving={saveExcluded.isPending}
          saveError={saveExcluded.error ? apiErrorText(saveExcluded.error) : null}
          onRetry={() => {
            void excluded.refetch();
            void charNames.refetch();
          }}
          onSave={(names) => saveExcluded.mutate(names)}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === "feed" && (
        <BottomSheet onClose={() => setSheet(null)}>
          <div className="max-h-[80dvh] overflow-y-auto p-4">
            <h3 className="mb-2 text-base font-semibold">Что считали</h3>
            {feed.error && (
              <p className="mb-2 text-sm text-red-700">{apiErrorText(feed.error)}</p>
            )}
            {feed.isLoading && <p className="text-sm text-slate-500">Загружаем…</p>}
            {feed.data && (
              <ScanList
                scans={feed.data}
                readOnly={readOnly}
                busy={dropScan.isPending || editQty.isPending}
                onDelete={(s) => dropScan.mutate(s.id)}
                onChangeQty={(s, qty) => editQty.mutate({ scanId: s.id, qty })}
                emptyText="Пока ничего не сканировали."
              />
            )}
          </div>
        </BottomSheet>
      )}

      {/* Карточка позиции: её сканы с автором и временем. Отсюда правят
          ошибку, замеченную не сразу, — герой к этому моменту давно сменился. */}
      {posSel && (
        <BottomSheet onClose={() => setPosSel(null)}>
          <div className="max-h-[80dvh] overflow-y-auto p-4">
            <h3 className="text-base font-semibold leading-tight">
              {selected?.nomenclature_name || posSel.title || "Позиция"}
            </h3>
            <div className="text-xs text-slate-500">
              {selected?.nomenclature_article}
              {selected?.characteristic_name ? ` · ${selected.characteristic_name}` : ""}
              {selected?.partia_name ? ` · партия ${selected.partia_name}` : ""}
            </div>
            {selected && (
              <div className="mt-2 text-sm">
                <span className="font-semibold">{selected.qty_scanned}</span>
                <span className="text-slate-500">
                  {" "}
                  {selected.unit_name} насчитано
                  {selected.unexpected
                    ? " · в учёте этой позиции нет"
                    : ` · по учёту ${selected.qty_accounted}`}
                </span>
              </div>
            )}
            <div className="mt-3">
              {posScans.error && (
                <p className="mb-2 text-sm text-red-700">{apiErrorText(posScans.error)}</p>
              )}
              {posScans.isLoading && <p className="text-sm text-slate-500">Загружаем…</p>}
              {posScans.data && (
                <ScanList
                  scans={posScans.data}
                  readOnly={readOnly}
                  busy={dropScan.isPending || editQty.isPending}
                  onDelete={(s) => dropScan.mutate(s.id)}
                  onChangeQty={(s, qty) => editQty.mutate({ scanId: s.id, qty })}
                  emptyText="По этой позиции ещё не сканировали."
                />
              )}
            </div>
          </div>
        </BottomSheet>
      )}

      {sheet === "workers" && (
        <BottomSheet onClose={() => setSheet(null)}>
          <div className="max-h-[70dvh] overflow-y-auto p-4">
            <h3 className="mb-2 text-base font-semibold">Кто считает</h3>
            <p className="mb-2 text-xs text-slate-500">
              Смена общая: считать можно с любого числа устройств одновременно.
            </p>
            <div className="space-y-1">
              {workers.data?.map((w) => (
                <div key={w.username} className="flex justify-between rounded border p-2 text-sm">
                  <span>{w.username}</span>
                  <span className="text-slate-500">
                    {w.scans} сканов
                    {w.last_at ? ` · ${new Date(w.last_at).toLocaleTimeString("ru-RU")}` : ""}
                  </span>
                </div>
              ))}
              {workers.data?.length === 0 && (
                <p className="text-sm text-slate-500">Пока никто не сканировал.</p>
              )}
            </div>
          </div>
        </BottomSheet>
      )}

      {confirm === "finish" && (
        <Dialog
          title="Завершить инвентаризацию?"
          body={
            <div className="space-y-2 text-sm">
              {/* Повторное завершение ОБНОВЛЯЕТ тот же документ, а не заводит
                  новый: раньше «вернуть в работу» гасило документ пометкой на
                  удаление, и склад видел это как «портал удалил инвентаризацию
                  и создал другую». */}
              <p>
                {d?.one_c_doc_number
                  ? `Документ ${d.one_c_doc_number} будет обновлён: в него уйдёт то, что посчитано сейчас. Номер останется прежним.`
                  : `В 1С уйдёт документ инвентаризации по складу «${d?.warehouse_name}» — непроведённый.`}
              </p>
              {(pr?.positions_untouched ?? 0) > 0 && (
                <p className="rounded bg-amber-100 p-2 text-amber-900">
                  Не посчитано позиций: {pr?.positions_untouched}. В документ они не
                  попадут — иначе каждая из них стала бы недостачей.
                </p>
              )}
              {split.length > 0 && (
                <div className="rounded bg-amber-100 p-2 text-amber-900">
                  <p>
                    Номенклатур, посчитанных в нескольких вариантах: {split.length}. В
                    документе каждый вариант будет отдельной строкой:
                  </p>
                  <ul className="mt-1 space-y-1">
                    {split.slice(0, SPLIT_SHOWN).map((g) => (
                      <li key={g.nomenclature_ref}>
                        <span className="font-medium">{g.nomenclature_name}</span>:{" "}
                        {g.variants.map(splitVariantText).join("; ")}
                      </li>
                    ))}
                  </ul>
                  {split.length > SPLIT_SHOWN && (
                    <p className="mt-1">и ещё {split.length - SPLIT_SHOWN}.</p>
                  )}
                  <p className="mt-1">
                    Так бывает и правильно — характеристики и партии разные. Если
                    вариант выбрали по ошибке, откройте позицию в списке и удалите
                    лишние сканы.
                  </p>
                </div>
              )}
            </div>
          }
          confirmLabel={d?.one_c_doc_number ? "Обновить документ" : "Создать документ"}
          confirmBusy={finish.isPending}
          onConfirm={() => finish.mutate()}
          onCancel={() => setConfirm(null)}
        />
      )}

      {confirm === "reopen" && (
        <Dialog
          title="Вернуть в работу?"
          body={
            <div className="space-y-2 text-sm">
              <p>
                Посчитанное останется, можно досчитать и завершить снова. Документ{" "}
                {d?.one_c_doc_number} в 1С останется на месте и при повторном завершении
                будет обновлён — новый номер не появится.
              </p>
              <p className="rounded bg-amber-100 p-2 text-amber-900">
                Если документ правили руками в 1С, правки затрёт составом из портала.
              </p>
            </div>
          }
          confirmLabel="Вернуть"
          confirmBusy={reopen.isPending}
          onConfirm={() => reopen.mutate()}
          onCancel={() => setConfirm(null)}
        />
      )}

      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-[80] bg-black">
            <CameraScanner
              onDetect={(raw) => {
                setCameraOpen(false);
                // ⚠️ Пока предыдущий скан в полёте, новый не шлём — камера
                // ловит код повторно так же легко, как ТСД, а каждая посылка
                // становится отдельной строкой пересчёта. Молча проглотить
                // код нельзя: человек видел бы закрывшуюся камеру и решил,
                // что товар посчитан.
                if (scanning) {
                  setBanner({
                    kind: "warn",
                    text: "Предыдущий скан ещё записывается — отсканируйте ещё раз.",
                  });
                  return;
                }
                scan.mutate(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

/**
 * Вопрос о партии — как в приёмке возвратов: номер вводят руками (он напечатан
 * на этикетке) или сканируют, портал ищет его в 1С ПОД номенклатурой.
 *
 * Не нашли — говорим об этом прямо и предлагаем «принять без партии». Чужую
 * партию подставлять нельзя: расхождение уедет в документ, и разбирать его
 * будут в учёте, а не здесь.
 */
function PartiaAsk({
  result,
  busy,
  onSubmit,
  onWithout,
}: {
  result: InventoryScanResult;
  busy: boolean;
  onSubmit: (batch: string) => void;
  onWithout: () => void;
}): React.ReactElement {
  const [value, setValue] = React.useState(result.partia_name || "");
  const notFound = Boolean(result.partia_name);
  return (
    <>
      <h3 className="text-base font-semibold">Какая партия?</h3>
      <p className={"mt-1 text-xs " + (notFound ? "text-red-600" : "text-slate-500")}>
        {notFound
          ? result.message
          : "Номер напечатан на этикетке. Портал найдёт партию в 1С."}
      </p>
      <input
        autoFocus
        inputMode="text"
        className="mt-3 w-full rounded-lg border p-3 text-base"
        placeholder="номер партии"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Сканер шлёт Enter в конце кода — принимаем его как «Найти».
          if (e.key === "Enter" && value.trim()) onSubmit(value.trim());
        }}
      />
      <Button
        className="mt-3 h-12 w-full"
        disabled={busy || !value.trim()}
        onClick={() => onSubmit(value.trim())}
      >
        Найти в 1С
      </Button>
      <Button
        variant="outline"
        className="mt-2 h-12 w-full"
        disabled={busy}
        onClick={onWithout}
      >
        Принять без партии
      </Button>
    </>
  );
}
